import { useRef } from 'react'
import { Loader2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export interface ClearDictionaryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  wordCount: number
  /** The clear is running: both buttons are disabled and the dialog stays open until it answers. */
  isClearing?: boolean
  onConfirm: () => void
  /** Where focus goes on close: the dialog has no trigger, so the caller decides. */
  onCloseAutoFocus?: (event: Event) => void
}

/**
 * Figma "Clear custom dictionary" confirmation (shadcn AlertDialog). Initial focus is on Cancel,
 * the safe choice; X, Cancel and Esc close it without confirming. Confirming doesn't close it: the
 * caller closes it once the clear has answered.
 */
export function ClearDictionaryDialog({
  open,
  onOpenChange,
  wordCount,
  isClearing = false,
  onConfirm,
  onCloseAutoFocus,
}: ClearDictionaryDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null)

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        showCloseButton
        // The X is a Cancel too and renders last, so Radix would focus it; Figma wants Cancel.
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          cancelRef.current?.focus()
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Clear custom dictionary?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes all {wordCount} {wordCount === 1 ? 'word' : 'words'} from the school&apos;s
            custom dictionary. Roster names aren&apos;t affected. This can&apos;t be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel ref={cancelRef} disabled={isClearing}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isClearing}
            aria-busy={isClearing || undefined}
            onClick={(event) => {
              // Radix closes the dialog on Action; keep it open until the clear has answered.
              event.preventDefault()
              onConfirm()
            }}
          >
            {isClearing && <Loader2 className="animate-spin" />}
            Clear All
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
